import httpClient from "@plugins/http";
import { ApiResponse } from "@apis/api.ts";
import { ResourceTaggingRemoveApiModel } from "@modules/manage-resource/components/bulk-module-tag.types.ts";

/**
 * Mirrors the backend ResourceTaggingApiModel request body for
 * append / remove lesson tagging endpoints.
 */
export interface ResourceTaggingParams {
  resourceTaggingSubjectLevelList: object[];
  resourceTaggingContentMapList: object[];
  hiddenResourceTaggingContentMapList?: object[];
}

/**
 * TODO: [API] clean up the usage, this API is misleading and contains misc method. Check with TL if you need to add new method here.
 */
export default {
  /**
   * resource tagging api
   */
  fetchResourceTaggings: (uuid, resourceType, resourceStatus, includeExpired) => {
    const timeStamp = Date.now();
    return httpClient.get(
      `/apis/resource/tagging/${resourceType}/${uuid}?status=${resourceStatus}&includeExpired=${includeExpired}&t=${timeStamp}`,
    );
  },
  saveResourceTaggings: (uuid, resourceType, resourceStatus, params) => {
    return httpClient.post(
      `/apis/resource/tagging/save/${resourceType}/${uuid}/${resourceStatus}`,
      params,
    );
  },
  appendLessonTaggings: (resourceUuid: string, params: ResourceTaggingParams) => {
    return httpClient.post(`/apis/resource/tagging/append/LESSON/${resourceUuid}`, params);
  },
  removeLessonTaggings: (
    resourceUuid: string,
    params: ResourceTaggingRemoveApiModel,
  ): Promise<ApiResponse<boolean>> => {
    return httpClient.post(`/apis/resource/tagging/remove/LESSON/${resourceUuid}`, params);
  },

  /**
   * resource contributor api
   */
  fetchContributors: (uuid, resourceDomain) => {
    return httpClient.get(`/apis/resource-contributor`, {
      params: {
        resourceUuid: uuid,
        resourceDomain: resourceDomain,
      },
    });
  },
  saveContributor: (uuid, resourceDomain, contributorList) => {
    return httpClient.post(`/apis/resource-contributor/save`, {
      resourceUuid: uuid,
      resourceDomain: resourceDomain,
      contributorList: contributorList,
    });
  },
  addCurrentUserAsContributor: (lessonUuid) => {
    return httpClient.post(`/apis/lesson/contribute/${lessonUuid}`);
  },

  /**
   * resource share api
   */
  fetchCollaborators: (uuid) => httpClient.get(`/apis/shareresource/list/${uuid}`),
  saveCollaborator: (params) => {
    return httpClient.post(`/apis/shareresource/save`, params);
  },
  unshareLesson: (uuid) => {
    return httpClient.post(`/apis/shareresource/unshare/${uuid}`, {}, {});
  },

  /**
   * resource thumbnail api
   */
  addThumbnail: (identifier, resourceType, requestData) => {
    const params = new URLSearchParams();
    params.append("imageType", requestData.imageType);
    params.append("mediaObjectUuid", requestData.mediaObjectUuid);
    return httpClient.post(`/apis/resourcethumbnail/add/${resourceType}/${identifier}`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },
  uploadThumbnail: (identifier, resourceType, requestData) => {
    return httpClient.post(
      `/apis/resourcethumbnail/upload/${resourceType}/${identifier}`,
      requestData,
    );
  },
  removeThumbnail: (identifier, resourceType, requestData) => {
    const params = new URLSearchParams();
    params.append("imageType", requestData.imageType);
    return httpClient.post(`/apis/resourcethumbnail/remove/${resourceType}/${identifier}`, params, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  },

  /**
   * resource assign api
   */
  checkResourceAssignable: (
    type: string,
    uuid: string,
  ): Promise<
    ApiResponse<{ isAssignable: boolean; adaptiveSectionLearningOutcomeError?: boolean }>
  > => {
    return httpClient.get(`/apis/assignment/assignable/${type}/${uuid}`);
  },

  /**
   * resource context api
   */
  fetchResourceContext: (requestData) => {
    return httpClient.get(
      `/apis/resource/context/${requestData.type}/${requestData.uuid}?raw=${requestData.raw}`,
    );
  },

  /**
   * resource metadata api
   */
  fetchResourceMetadataList: (params) => {
    return httpClient.get(`/apis/resource/metadata/list?${params}`);
  },
};
